import React, { useState } from 'react'
import { Drawer, Box, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { AtlasButton } from 'components/atlas-material/button'
import { AddActionEditActionButton } from 'components/shared/StyledComponents'
import { setUserPreferredTemplate } from 'businesslayer/networkrequests/templateService'
import i18n from 'common/util/internationalization'
import { ColorBaseMulberryBlue, ColorBaseWhite, ColorEastBay } from 'assets/styles/variables'
import TemplatePreviewPage from 'pages/template-preview-page'

/**
 * TemplatePreviewDrawer - Drawer version of TemplatePreviewModal
 * This component provides a better user experience on iPad and mobile devices
 * by using a drawer instead of a modal, which aligns better with the ExportDrawer
 */
const DRAWER_WIDTH = 658

const useStyles = makeStyles((theme) => ({
    drawer: {
        width: DRAWER_WIDTH,
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        },
        // iPad specific responsive behavior
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            width: '100vw'
        }
    },
    drawerPaper: {
        position: 'fixed',
        width: DRAWER_WIDTH,
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        },
        // iPad specific responsive behavior
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            width: '100vw'
        }
    },
    drawerContent: {
        width: '100%',
        height: '100vh',
        outline: 'none',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // iPad specific - use percentage instead of viewport height
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            height: '100%'
        }
    },
    exportOptionsSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        flex: 1,
        overflow: 'hidden',
        height: '100%'
    },
    footer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '16px',
        alignSelf: 'stretch',
        height: '72px',
        backgroundColor: '#fff',
        zIndex: theme.zIndex.drawer + 1,
        flexShrink: 0
    },
    footerComp: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        padding: theme.spacing(2),
        gap: '16px',
        position: 'absolute',
        right: 0
    },
    saveTemplate: {
        textTransform: 'none',
        backgroundColor: ColorEastBay,
        display: 'flex',
        color: '#fff',
        borderWidth: '0px',
        borderRadius: '3px',
        letterSpacing: '0.5px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        padding: '8px 12px',
        fontWeight: 600,
        placeContent: 'center',
        gap: '8px',
        minWidth: '78px',
        '&:focus, &:active': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px',
            backgroundColor: `${ColorEastBay}`
        },
        '&:hover': {
            backgroundColor: '#364262',
            color: '#fff',
            cursor: 'pointer'
        }
    }
}))

interface TemplatePreviewDrawerProps {
    open: boolean
    onClose: () => void
    imageUrlsData: string[]
    loading: boolean
    onCancel: () => void
    templateOptions?: any[]
    templateOption?: any
    systemTemplate?: any
    setTemplateOption?: (template: any) => void
    handleLoadMore?: () => void
    isLoading?: boolean
    hasMoreTemplates?: boolean
    handleGetTemplatePreview?: (templateId: string) => void
    previewSelection?: any
    setPreviewSelection?: (template: any) => void
    userPreferredTemplateResponse?: any
}

const TemplatePreviewDrawer: React.FC<TemplatePreviewDrawerProps> = ({
    open,
    onClose,
    imageUrlsData,
    loading,
    onCancel,
    systemTemplate,
    templateOptions,
    templateOption,
    setTemplateOption,
    handleLoadMore,
    isLoading,
    hasMoreTemplates,
    handleGetTemplatePreview,
    previewSelection,
    setPreviewSelection,
    userPreferredTemplateResponse
}) => {
    const classes = useStyles()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleCancel = () => {
        onCancel()
        onClose()
        setPreviewSelection?.({}) // Reset previewSelection when drawer is closed
    }

    const handleClose = () => {
        onClose()
        setPreviewSelection?.({}) // Reset previewSelection when drawer is closed
    }

    const handleSaveTemplate = async () => {
        setIsSubmitting(true)

        if (onCancel) {
            onCancel()
        } else {
            handleClose()
        }

        // Update the actual templateOption with the previewSelection when save is clicked
        if (previewSelection && Object.keys(previewSelection).length > 0) {
            setTemplateOption?.(previewSelection)
        }

        // Save user's preferred template with saved export option or 'minutesOnly' as fallback
        if (previewSelection && Object.keys(previewSelection).length > 0) {
            try {
                const templateId = previewSelection.id

                // Use saved export option if available, else use minutesOnly
                let exportType = 'minutesOnly' // Default fallback
                if (userPreferredTemplateResponse?.preferredExportOption) {
                    exportType = userPreferredTemplateResponse.preferredExportOption
                }

                await setUserPreferredTemplate(templateId, exportType)
            } catch (error) {
                console.error('Failed to update preferred template:', error)
            }
        }

        // Reset previewSelection after saving
        setPreviewSelection?.({})
        setIsSubmitting(false)
    }

    return (
        <Drawer
            className={classes.drawer}
            classes={{ paper: classes.drawerPaper }}
            anchor="right"
            open={open}
            onClose={onClose}
        >
            <Box className={classes.drawerContent}>
                <Box className={classes.exportOptionsSection}>
                    <TemplatePreviewPage
                        imageUrlsData={imageUrlsData}
                        loading={loading}
                        onClose={handleClose}
                        onCancel={handleCancel}
                        systemTemplate={systemTemplate}
                        templateOptions={templateOptions}
                        templateOption={templateOption}
                        setTemplateOption={setTemplateOption}
                        handleLoadMore={handleLoadMore}
                        isLoading={isLoading}
                        hasMoreTemplates={hasMoreTemplates}
                        handleGetTemplatePreview={handleGetTemplatePreview}
                        previewSelection={previewSelection}
                        setPreviewSelection={setPreviewSelection}
                        userPreferredTemplateResponse={userPreferredTemplateResponse}
                        hideFooter={true}
                        isDrawerContext={true}
                    />
                </Box>

                <Box className={classes.footer}>
                    <Box className={classes.footerComp}>
                        <AddActionEditActionButton
                            key="actionCancel"
                            data-analytics="MM-Template-Cancel"
                            disableFocusRipple
                            onClick={handleCancel}
                            disableRipple
                        >
                            {i18n.t('CANCEL')}
                        </AddActionEditActionButton>
                        <AtlasButton
                            data-analytics="MM-Template-Save"
                            role="button"
                            onKeyPress={() => {
                                console.log('onKeyPress')
                            }}
                            disableRipple
                            className={classes.saveTemplate}
                            key="actionSave"
                            disabled={isSubmitting}
                            type="submit"
                            onClick={handleSaveTemplate}
                            disableFocusRipple
                        >
                            {isSubmitting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                i18n.t('SAVE')
                            )}
                        </AtlasButton>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    )
}

export default TemplatePreviewDrawer
